<script setup lang="ts">
import type { HTMLAttributes } from 'vue';
import { Inbox } from 'lucide-vue-next';
import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/card';
import EntityArtworkFallback from '@/components/entity/EntityArtworkFallback.vue';

defineOptions({
    inheritAttrs: false,
});

const props = withDefaults(defineProps<{
    title?: string;
    description?: string;
    class?: HTMLAttributes['class'];
    embedded?: boolean;
    artwork?: boolean;
    artworkSeed?: string;
}>(), {
    title: 'No items found',
    description: 'Get started by creating your first item.',
    class: '',
    embedded: false,
    artwork: false,
    artworkSeed: '',
});
</script>

<template>
    <component
        :is="embedded ? 'div' : Card"
        v-bind="$attrs"
        :class="cn(
            'flex flex-col items-center justify-center gap-3 px-4 py-10 text-center',
            embedded ? 'border-0 bg-transparent shadow-none' : 'border-dashed shadow-none',
            props.class,
        )"
    >
        <div v-if="artwork" class="size-20 overflow-hidden rounded-2xl border border-border/60 shadow-sm">
            <EntityArtworkFallback :seed="artworkSeed || title" />
        </div>

        <div v-else class="inline-flex size-10 items-center justify-center rounded-full bg-primary/10 text-primary">
            <slot name="icon">
                <Inbox class="size-5" />
            </slot>
        </div>

        <div class="max-w-md space-y-2">
            <h3 class="text-base font-semibold tracking-tight">
                <slot name="title">{{ title }}</slot>
            </h3>
            <p v-if="description || $slots.description" class="text-sm leading-6 text-muted-foreground">
                <slot name="description">{{ description }}</slot>
            </p>
        </div>

        <div v-if="$slots.action" class="pt-1">
            <slot name="action" />
        </div>
    </component>
</template>
